import { useMemo } from 'react';
import { captureExceptionEvent } from '@blocs.observability';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '../Heap';
import { PaywallLocation } from './enums/PaywallLocation';

export type PaywallFunnelEvents = {
  trackCheckoutStarted: () => void;
  trackBillingAddressFieldStarted: () => void;
  trackBillingAddressAutofillUsed: () => void;
  trackBillingAddressCompleted: () => void;
  trackBillingAddressEditClicked: () => void;
  trackCheckoutSubmitted: () => void;
  trackCheckoutCompleted: (data?: Record<string, unknown>) => void;
  trackCheckoutAbandoned: () => void;
};

const SIGNUP_EVENT_NAMES: Record<keyof PaywallFunnelEvents, HEAP_EVENT_NAMES> = {
  trackCheckoutStarted: HEAP_EVENT_NAMES.SIGNUP_CHECKOUT_STARTED,
  trackBillingAddressFieldStarted: HEAP_EVENT_NAMES.SIGNUP_BILLING_ADDRESS_FIELD_STARTED,
  trackBillingAddressAutofillUsed: HEAP_EVENT_NAMES.SIGNUP_BILLING_ADDRESS_AUTOFILL_USED,
  trackBillingAddressCompleted: HEAP_EVENT_NAMES.SIGNUP_BILLING_ADDRESS_COMPLETED,
  trackBillingAddressEditClicked: HEAP_EVENT_NAMES.SIGNUP_BILLING_ADDRESS_EDIT_CLICKED,
  trackCheckoutSubmitted: HEAP_EVENT_NAMES.SIGNUP_CHECKOUT_SUBMITTED,
  trackCheckoutCompleted: HEAP_EVENT_NAMES.SIGNUP_CHECKOUT_COMPLETED,
  trackCheckoutAbandoned: HEAP_EVENT_NAMES.SIGNUP_CHECKOUT_ABANDONED,
};

const UPGRADE_EVENT_NAMES: Record<keyof PaywallFunnelEvents, HEAP_EVENT_NAMES> = {
  trackCheckoutStarted: HEAP_EVENT_NAMES.UPGRADE_CHECKOUT_STARTED,
  trackBillingAddressFieldStarted: HEAP_EVENT_NAMES.UPGRADE_BILLING_ADDRESS_FIELD_STARTED,
  trackBillingAddressAutofillUsed: HEAP_EVENT_NAMES.UPGRADE_BILLING_ADDRESS_AUTOFILL_USED,
  trackBillingAddressCompleted: HEAP_EVENT_NAMES.UPGRADE_BILLING_ADDRESS_COMPLETED,
  trackBillingAddressEditClicked: HEAP_EVENT_NAMES.UPGRADE_BILLING_ADDRESS_EDIT_CLICKED,
  trackCheckoutSubmitted: HEAP_EVENT_NAMES.UPGRADE_CHECKOUT_SUBMITTED,
  trackCheckoutCompleted: HEAP_EVENT_NAMES.UPGRADE_CHECKOUT_COMPLETED,
  trackCheckoutAbandoned: HEAP_EVENT_NAMES.UPGRADE_CHECKOUT_ABANDONED,
};

// Matches the belt-and-suspenders call-site pattern already established in BillingPopupContext.tsx's
// showBillingPopup — captureHeapEvent already try/catches internally around window.heap.track, but this
// also wraps it, so a bug constructing `data` can never break the paywall.
const track = (name: HEAP_EVENT_NAMES, data?: Record<string, unknown>) => {
  try {
    captureHeapEvent({ name, data });
  } catch (e) {
    captureExceptionEvent(e, { section: `Talent: heap "${name}" event` });
  }
};

const noop = () => undefined;

// No-ops every tracker — used when this Paywall render isn't actually part of the tracked funnel (e.g.
// the standalone /membership-billing page, which reuses the same checkout UI outside of both the
// Signup flow and the Upgrade modal).
const NOOP_EVENTS: PaywallFunnelEvents = {
  trackCheckoutStarted: noop,
  trackBillingAddressFieldStarted: noop,
  trackBillingAddressAutofillUsed: noop,
  trackBillingAddressCompleted: noop,
  trackBillingAddressEditClicked: noop,
  trackCheckoutSubmitted: noop,
  trackCheckoutCompleted: noop,
  trackCheckoutAbandoned: noop,
};

// Resolves which funnel bucket a Paywall render belongs to straight off the same PaywallLocation
// already threaded through every consumer — no separate 'signup'/'upgrade' vocabulary to keep in sync
// with it. Signup takes priority (registration never runs as a modal, so it's always tracked);
// otherwise 'upgrade' only when this really is the Upgrade modal (isModal, triggered via
// billingPopup.show()) as opposed to a full-page reuse of the same checkout UI — e.g.
// /membership-billing — that isn't part of either tracked funnel (falls through to NOOP_EVENTS).
const resolveEventNames = (paywallLocation: PaywallLocation | undefined, isModal: boolean | undefined) => {
  if (paywallLocation === PaywallLocation.SIGNUP) return SIGNUP_EVENT_NAMES;
  if (isModal) return UPGRADE_EVENT_NAMES;
  return undefined;
};

// Centralizes signup/upgrade prefix branching for the Paywall funnel events, so call sites never write
// `isRegistration ? HEAP_EVENT_NAMES.SIGNUP_X : HEAP_EVENT_NAMES.UPGRADE_X` themselves. Returns a single
// memoized object (stable as long as the resolved bucket doesn't change) so it threads through
// component props as one value, the same way `paymentMethodFormikRef` already does despite being a
// compound concern.
//
// `isModal` is optional so callers that are always signup (e.g. the registration-only AddPayment hook)
// can omit it — paywallLocation === SIGNUP already wins regardless.
export const usePaywallFunnelEvents = (
  paywallLocation: PaywallLocation | undefined,
  isModal?: boolean
): PaywallFunnelEvents => {
  const names = resolveEventNames(paywallLocation, isModal);
  return useMemo<PaywallFunnelEvents>(() => {
    if (!names) return NOOP_EVENTS;
    return {
      trackCheckoutStarted: () => track(names.trackCheckoutStarted),
      trackBillingAddressFieldStarted: () => track(names.trackBillingAddressFieldStarted),
      trackBillingAddressAutofillUsed: () => track(names.trackBillingAddressAutofillUsed),
      trackBillingAddressCompleted: () => track(names.trackBillingAddressCompleted),
      trackBillingAddressEditClicked: () => track(names.trackBillingAddressEditClicked),
      trackCheckoutSubmitted: () => track(names.trackCheckoutSubmitted),
      trackCheckoutCompleted: (data) => track(names.trackCheckoutCompleted, data),
      trackCheckoutAbandoned: () => track(names.trackCheckoutAbandoned),
    };
  }, [names]);
};
